import type { ButtonHTMLAttributes, AnchorHTMLAttributes, ReactNode } from 'react';
import { cn } from '../../lib/utils';

type BaseProps = {
  /**
   * Button content
   */
  children: ReactNode;

  /**
   * Visual style variant
   * @default 'primary'
   */
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger' | 'success';

  /**
   * Button size
   * @default 'md'
   */
  size?: 'sm' | 'md' | 'lg';

  /**
   * Loading state - shows spinner and disables interaction
   * @default false
   */
  loading?: boolean;

  /**
   * Full width button
   * @default false
   */
  fullWidth?: boolean;

  /**
   * Icon-only button (square with equal padding)
   * @default false
   */
  iconOnly?: boolean;

  /**
   * Additional CSS classes
   */
  className?: string;
};

type ButtonAsButton = BaseProps &
  Omit<ButtonHTMLAttributes<HTMLButtonElement>, keyof BaseProps> & {
    as?: 'button';
  };

type ButtonAsLink = BaseProps &
  Omit<AnchorHTMLAttributes<HTMLAnchorElement>, keyof BaseProps> & {
    as: 'a';
  };

export type ButtonProps = ButtonAsButton | ButtonAsLink;

/**
 * Button Component
 *
 * A flexible button component with multiple variants, sizes, and states.
 * Can be rendered as a button or anchor tag.
 * Follows WCAG 2.1 AA accessibility standards.
 *
 * @example
 * ```tsx
 * <Button variant="primary" size="md" onClick={handleClick}>
 *   Click me
 * </Button>
 *
 * <Button as="a" href="/contact" variant="primary">
 *   Contact Us
 * </Button>
 * ```
 */
export const Button = ({
  children,
  variant = 'primary',
  size = 'md',
  loading = false,
  fullWidth = false,
  iconOnly = false,
  className,
  as = 'button',
  ...props
}: ButtonProps) => {
  const Component = as;

  const baseStyles = 'inline-flex items-center justify-center rounded-md font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50';

  const variantStyles = {
    primary: 'bg-primary text-primary-foreground hover:bg-primary/90',
    secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
    outline: 'border border-input bg-background hover:bg-accent hover:text-accent-foreground',
    ghost: 'hover:bg-accent hover:text-accent-foreground',
    danger: 'bg-destructive text-destructive-foreground hover:bg-destructive/90',
    success: 'bg-green-600 text-white hover:bg-green-700',
  };

  const sizeStyles = {
    sm: iconOnly ? 'h-8 w-8 p-0' : 'h-8 px-3 text-xs',
    md: iconOnly ? 'h-9 w-9 p-0' : 'h-9 px-4 py-2 text-sm',
    lg: iconOnly ? 'h-10 w-10 p-0' : 'h-10 px-6 text-base',
  };

  const classNames = cn(
    baseStyles,
    variantStyles[variant],
    sizeStyles[size],
    fullWidth && 'w-full',
    loading && 'cursor-wait opacity-70',
    className
  );

  if (as === 'a') {
    return (
      <a className={classNames} {...(props as AnchorHTMLAttributes<HTMLAnchorElement>)}>
        {children}
      </a>
    );
  }

  const isDisabled = (props as ButtonHTMLAttributes<HTMLButtonElement>).disabled || loading;

  return (
    <button
      className={classNames}
      disabled={isDisabled}
      aria-busy={loading}
      {...(props as ButtonHTMLAttributes<HTMLButtonElement>)}
    >
      {children}
    </button>
  );
};
